import React from 'react';
import {
    Security as Shield,
    PrivacyTip,
    Lock,
    Visibility,
    PersonAdd,
    Storage,
    Share,
    Delete
} from '@mui/icons-material';

const PrivacyPolicyPage = () => {
    const lastUpdated = "December 1, 2024";

    const dataTypes = [
        {
            icon: <PersonAdd style={{ fontSize: 24, color: '#2563eb' }} />,
            title: "Account Information",
            description: "Name, email, company details, and account preferences"
        },
        {
            icon: <Storage style={{ fontSize: 24, color: '#16a34a' }} />,
            title: "Usage Data",
            description: "How you interact with our platform, features used, and performance metrics"
        },
        {
            icon: <Shield style={{ fontSize: 24, color: '#7c3aed' }} />,
            title: "Risk Assessment Data",
            description: "Data you input for risk analysis, results, and generated reports"
        },
        {
            icon: <Visibility style={{ fontSize: 24, color: '#dc2626' }} />,
            title: "Technical Information",
            description: "IP address, browser type, device information, and log data"
        }
    ];

    const sections = [
        {
            title: "1. Information We Collect",
            content: [
                "We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.",
                "Account Information: Name, email address, company name, job title, and billing information.",
                "Risk Assessment Data: Any data you input into our platform for analysis, including documents, datasets, and configuration settings.",
                "Usage Information: How you interact with our service, features you use, and performance metrics.",
                "Technical Information: IP address, browser type, operating system, device information, and access logs.",
                "Communication Data: Records of your communications with us, including support tickets and feedback."
            ]
        },
        {
            title: "2. How We Use Your Information",
            content: [
                "We use the information we collect to:",
                "• Provide, maintain, and improve our services",
                "• Process transactions and send related information",
                "• Send technical notices, updates, and support messages",
                "• Respond to your comments and questions",
                "• Analyze usage patterns to improve our platform",
                "• Detect, investigate, and prevent security incidents",
                "• Comply with legal obligations and enforce our terms"
            ]
        },
        {
            title: "3. Information Sharing and Disclosure",
            content: [
                "We do not sell, trade, or otherwise transfer your personal information to third parties except as described in this policy.",
                "Service Providers: We may share information with trusted third-party service providers who help us operate our platform, subject to confidentiality agreements.",
                "Legal Requirements: We may disclose information if required by law, regulation, or legal process.",
                "Business Transfers: Information may be transferred in connection with a merger, acquisition, or sale of assets.",
                "Protection of Rights: We may disclose information to protect our rights, property, or safety, or that of our users or others.",
                "With Your Consent: We may share information for other purposes with your explicit consent."
            ]
        },
        {
            title: "4. Data Security",
            content: [
                "We implement comprehensive security measures to protect your information:",
                "• End-to-end encryption for data in transit and at rest",
                "• Multi-factor authentication and access controls",
                "• Regular security audits and penetration testing",
                "• SOC 2 Type II compliance and ISO 27001 certification",
                "• Employee background checks and security training",
                "• Incident response procedures and breach notification protocols",
                "However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but continuously work to protect your data."
            ]
        },
        {
            title: "5. Data Retention",
            content: [
                "We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this policy.",
                "Account Data: Retained while your account is active and for a reasonable period afterward.",
                "Risk Assessment Data: Retained according to your subscription plan and data retention settings.",
                "Usage Logs: Typically retained for 12 months for security and analytics purposes.",
                "Communication Records: Retained for 3 years for support and legal purposes.",
                "You can request deletion of your data at any time, subject to legal and contractual obligations."
            ]
        },
        {
            title: "6. Your Rights and Choices",
            content: [
                "You have several rights regarding your personal information:",
                "• Access: Request a copy of the personal information we hold about you",
                "• Correction: Update or correct inaccurate information",
                "• Deletion: Request deletion of your personal information",
                "• Portability: Export your data in a structured, machine-readable format",
                "• Restriction: Limit how we process your information",
                "• Objection: Object to certain types of processing",
                "• Withdraw Consent: Withdraw previously given consent at any time",
                "To exercise these rights, contact us at privacy@riskas.com or through your account settings."
            ]
        },
        {
            title: "7. Cookies and Tracking Technologies",
            content: [
                "We use cookies and similar technologies to improve your experience:",
                "Essential Cookies: Required for basic platform functionality and security.",
                "Analytics Cookies: Help us understand how you use our platform to improve our services.",
                "Preference Cookies: Remember your settings and preferences.",
                "Marketing Cookies: Used to deliver relevant advertising and measure campaign effectiveness.",
                "You can control cookie preferences through your browser settings, but disabling certain cookies may affect platform functionality."
            ]
        },
        {
            title: "8. International Data Transfers",
            content: [
                "Your information may be transferred to and processed in countries other than your country of residence.",
                "We ensure adequate protection through:",
                "• Standard Contractual Clauses approved by relevant authorities",
                "• Adequacy decisions by regulatory bodies",
                "• Other legally recognized transfer mechanisms",
                "We maintain the same level of protection regardless of where your data is processed."
            ]
        },
        {
            title: "9. Children's Privacy",
            content: [
                "RiskAS is not intended for use by individuals under the age of 18.",
                "We do not knowingly collect personal information from children under 18.",
                "If we learn that we have collected information from a child under 18, we will take steps to delete such information promptly.",
                "If you believe we have collected information from a child under 18, please contact us immediately."
            ]
        },
        {
            title: "10. Changes to This Policy",
            content: [
                "We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.",
                "We will notify you of material changes by:",
                "• Posting the updated policy on our website",
                "• Sending an email notification to registered users",
                "• Displaying a prominent notice in our platform",
                "Your continued use of our services after changes become effective constitutes acceptance of the updated policy."
            ]
        }
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            {/* Header Spacer */}
            <div style={{ height: '64px' }}></div>

            {/* Hero Section */}
            <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <PrivacyTip style={{ fontSize: 48, color: '#2563eb' }} />
                    </div>
                    <h1 style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        marginBottom: '24px',
                        color: '#111827'
                    }}>
                        Privacy Policy
                    </h1>
                    <p style={{
                        fontSize: '20px',
                        color: '#6b7280',
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                    }}>
                        Your privacy is fundamental to how we operate. This policy explains how we collect,
                        use, and protect your information when you use RiskAS.
                    </p>
                    <div style={{
                        marginTop: '32px',
                        padding: '16px 24px',
                        background: 'rgba(37, 99, 235, 0.1)',
                        borderRadius: '8px',
                        display: 'inline-block'
                    }}>
                        <span style={{ color: '#2563eb', fontWeight: '600' }}>
                            Last updated: {lastUpdated}
                        </span>
                    </div>
                </div>
            </section>

            {/* Privacy Principles */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', textAlign: 'center' }}>
                        Our Privacy Principles
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <Lock style={{ fontSize: 32, color: '#16a34a', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                                Secure by Design
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                Built with privacy and security at the core
                            </p>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <Visibility style={{ fontSize: 32, color: '#2563eb', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                                Transparent
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                Clear about what we collect and why
                            </p>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <PersonAdd style={{ fontSize: 32, color: '#7c3aed', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                                User Control
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                You control your data and privacy settings
                            </p>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <Delete style={{ fontSize: 32, color: '#dc2626', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                                Data Minimization
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                We only collect what's necessary
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Data Types */}
            <section style={{ padding: '60px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', textAlign: 'center' }}>
                        Types of Information We Collect
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                        {dataTypes.map((type, index) => (
                            <div key={index} style={{
                                background: 'white',
                                padding: '24px',
                                borderRadius: '12px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                                    {type.icon}
                                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginLeft: '12px' }}>
                                        {type.title}
                                    </h3>
                                </div>
                                <p style={{ color: '#6b7280', lineHeight: '1.5' }}>
                                    {type.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Privacy Content */}
            <section style={{ padding: '80px 0', background: 'white' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{
                        background: '#f9fafb',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }}>
                        {sections.map((section, index) => (
                            <div key={index} style={{
                                padding: '32px',
                                borderBottom: index < sections.length - 1 ? '1px solid #e5e7eb' : 'none'
                            }}>
                                <h2 style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#111827',
                                    marginBottom: '16px'
                                }}>
                                    {section.title}
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {section.content.map((paragraph, pIndex) => (
                                        <p key={pIndex} style={{
                                            color: '#374151',
                                            lineHeight: '1.6',
                                            fontSize: '16px',
                                            margin: 0
                                        }}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* Contact Section */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                        Privacy Questions?
                    </h2>
                    <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
                        Our Privacy Team is here to help. Contact us with any questions about how we handle your data.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button
                            onClick={() => window.location.href = 'mailto:privacy@riskas.com'}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Email Privacy Team
                        </button>
                        <button
                            onClick={() => window.location.href = '/cookie-policy'}
                            style={{
                                background: 'white',
                                color: '#2563eb',
                                border: '2px solid #2563eb',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Cookie Policy
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicyPage;